import React, { FC, memo } from 'react';
import { Consumer } from '../consumer.types';
import cls from './ConsumerCardSubInfo.module.scss';
import { convertDateToNeedFormat } from '../../../utils/convertDateHandlers';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { getIsAtLeastAdmin } from '../../../selectors/selectors';
import { openModal } from '../../../store/slices/modalSlice';
import { openOffcanvas } from '../../../store/slices/offcanvasSlice';
import Button from '../../../UIComponents/Button';
import { MdLockOpen, MdLock, MdEuro, MdOutlineToll } from 'react-icons/md';
import { formatLangId } from '../consumer.utils';

type ConsumerCardSubInfoProps = {
  consumer: Consumer;
  balance: string;
  resetHandler: () => void;
};

const ConsumerCardSubInfo: FC<ConsumerCardSubInfoProps> = memo((props) => {
  const { consumer, balance, resetHandler } = props;
  const dispatch = useAppDispatch();
  const isAtLeastAdmin = useAppSelector(getIsAtLeastAdmin);
  const isB2B = consumer.consumerType === 'CONSUMER_B2B';
  const unBlockConsumerHandler = () =>
    dispatch(
      openModal({
        type: 'unLockConsumer',
        extra: {
          id: consumer?.id,
          title: 'Unblock client?',
          supportText: 'Are you sure you want to unblock',
          textInformation: consumer?.email,
          subSupportText: 'Client data and access will be restored.',
          actionButtonText: 'Unblock',
        },
      }),
    );
  const blockConsumerHandler = () =>
    dispatch(
      openModal({
        type: 'lockConsumer',
        extra: {
          id: consumer?.id,
          title: 'Block client?',
          supportText: 'Are you sure you want to block',
          textInformation: consumer?.email,
          subSupportText: 'Blocked account can be restored, data will be saved.',
          actionButtonText: 'Block',
        },
      }),
    );
  const transactionHandler = () =>
    dispatch(
      openModal({
        type: 'makeTransaction',
        extra: {
          id: consumer?.id,
          handler: resetHandler,
          title: 'Make transaction?',
          supportText: 'Funds will be added or deducted for account ',
          textInformation: consumer?.email,
          actionButtonText: 'Execute',
        },
      }),
    );
  const tokensHandler = () =>
    dispatch(
      openModal({
        type: 'adjustTokens',
        extra: {
          id: consumer?.id,
          handler: resetHandler,
          title: 'Adjust tokens?',
          supportText: 'Tokens will be added or deducted for account ',
          textInformation: consumer?.email,
          actionButtonText: 'Execute',
        },
      }),
    );
  const openReferrer = () =>
    dispatch(openOffcanvas({ type: 'consumerOffcanvas', extra: { id: consumer.refConsumerId } }));

  return (
    <div className={cls.ConsumerCardSubInfo}>
      <div className={cls.Info}>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Status</span>
          <span
            className={
              consumer.emailVerified
                ? `${cls.FieldValue} ${cls.FieldValueGreen}`
                : `${cls.FieldValue} ${cls.FieldValueRed}`
            }
          >
            {consumer.emailVerified ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Registration date</span>
          <span className={cls.FieldValue}>
            {convertDateToNeedFormat({ date: consumer.createdAt, format: 'datetime' })}
          </span>
        </div>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Timezone</span>
          <span className={cls.FieldValue}>
            {consumer.zoneGMT ? `GMT+${consumer.zoneGMT}` : 'Not specified'}
          </span>
        </div>
        <div className={cls.Property}>
          <span className={cls.FieldName}>Language</span>
          <span className={cls.FieldValue}>{formatLangId(consumer.langId)}</span>
        </div>
        {consumer.refConsumerId && (
          <div className={cls.Property}>
            <span className={cls.FieldName}>Invited by</span>
            <button
              type="button"
              className={`${cls.FieldValue} ${cls.FieldLink}`}
              onClick={openReferrer}
              data-testid="consumer-referrer"
            >
              {consumer.refConsumerEmail || consumer.refConsumerId}
            </button>
          </div>
        )}
        {isB2B && (
          <div className={cls.Property}>
            <span className={cls.FieldName}>Balance</span>
            <span
              className={
                Number(balance) <= 0 ? `${cls.FieldValue} ${cls.FieldValueRed}` : cls.FieldValue
              }
            >
              {balance} &#x20bd;
            </span>
          </div>
        )}
      </div>
      <div className={cls.Buttons}>
        <Button
          clickHandler={consumer?.blocked ? unBlockConsumerHandler : blockConsumerHandler}
          Icon={consumer?.blocked ? <MdLockOpen /> : <MdLock />}
          text={consumer?.blocked ? 'Unblock' : 'Block'}
          className={cls.Button}
        />
        {isAtLeastAdmin && (
          <Button
            clickHandler={isB2B ? transactionHandler : tokensHandler}
            Icon={isB2B ? <MdEuro /> : <MdOutlineToll />}
            text={isB2B ? 'Transactions' : 'Tokens'}
            className={cls.Button}
          />
        )}
      </div>
    </div>
  );
});

export default ConsumerCardSubInfo;
